import React, { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { FaTimes } from "react-icons/fa";
import { MyButton } from "../components/ButtonUtils";

const Preview = ({
  event,
  suspension,
  isSuperAdmin,
  editRoute,
  onEdit,
  setShowDeleteModal,
  setShowPreview,
}) => {
  // ---- small, local UX helpers (no core logic changes) ----
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setShowPreview(false);
    };
    // lock scroll behind modal
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [setShowPreview]);

  const display = (v) => (v === null || v === undefined || v === "" ? "-" : v);

  const formatted = useMemo(() => {
    const fDate = (d) => (d ? new Date(d).toLocaleDateString() : "-");
    const fTime = (t) => (t ? t : "-");
    return {
      startDate: fDate(event?.start_date),
      endDate: fDate(event?.end_date),
      startTime: fTime(event?.start_time),
      endTime: fTime(event?.end_time),
      statusLabel: event?.status === 1 ? "Public" : "Private",
      statusClass:
        event?.status === 1
          ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
          : "bg-amber-50 text-amber-700 ring-amber-600/20",
      mapHref:
        event?.latitude && event?.longitude
          ? `https://www.google.com/maps?q=${encodeURIComponent(
            `${event.latitude},${event.longitude}`
          )}`
          : null,
      prices: Array.isArray(event?.Pricings) ? event.Pricings : [],
    };
  }, [event]);

  const fields = useMemo(
    () => [
      { label: "Title", value: display(event?.title) },
      { label: "Venue", value: display(event?.venue) },
      { label: "Venue capacity", value: display(event?.venue_capacity) },
      { label: "Start date", value: formatted.startDate },
      { label: "End date", value: formatted.endDate },
      { label: "Start time", value: formatted.startTime },
      { label: "End time", value: formatted.endTime },
      { label: "Email", value: display(event?.email) },
      { label: "Phone number", value: display(event?.phone_number) },
      { label: "Facebook", value: display(event?.facebook) },
      { label: "Twitter", value: display(event?.twitter) },
      { label: "Instagram", value: display(event?.instagram) },
      { label: "YouTube", value: display(event?.youtube) },
      { label: "Hashtags", value: display(event?.hashtags) },
      { label: "Google map code", value: display(event?.google_map_code) },
      { label: "Latitude", value: display(event?.latitude) },
      { label: "Longitude", value: display(event?.longitude) },
      { label: "Formatted address", value: display(event?.formatted_address) },
      { label: "Description", value: display(event?.description) },
    ],
    [event, formatted]
  );

  return (
    <div
      className="fixed inset-0 z-[999999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setShowPreview(false)} // backdrop close
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="preview-title"
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()} // prevent close when clicking content
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-b from-white to-gray-50">
          <div className="min-w-0">
            <h2 id="preview-title" className="text-base font-semibold text-gray-900 truncate">
              Preview
            </h2>
            <p className="mt-0.5 text-xs text-gray-500 truncate">
              {event?.title || "Untitled event"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ${formatted.statusClass}`}
              title="Visibility"
            >
              {formatted.statusLabel}
            </span>
            <button
              onClick={() => setShowPreview(false)}
              className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-gray-900 text-white hover:bg-gray-800 transition"
              aria-label="Close preview"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Media */}
          <div className="w-full">
            <img
              src={event?.thumbnail}
              alt={event?.title}
              className="w-full h-48 md:h-56 object-cover rounded-xl ring-1 ring-gray-200"
            />
          </div>

          {/* Meta grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((item, i) => (
              <div key={i} className="flex flex-col gap-1 min-w-0">
                <dt className="text-[11px] uppercase tracking-wide text-gray-500">
                  {item.label}
                </dt>
                <dd className="text-sm text-gray-900 break-words">
                  {/* Turn likely URLs into links, otherwise plain text */}
                  {typeof item.value === "string" && /^https?:\/\//i.test(item.value) ? (
                    <a
                      href={item.value}
                      target="_blank"
                      rel="noreferrer"
                      className="underline decoration-gray-300 hover:decoration-gray-700 underline-offset-4"
                    >
                      {item.value}
                    </a>
                  ) : (
                    item.value
                  )}
                </dd>
              </div>
            ))}
          </div>

          {/* Map quick link (small local enhancement) */}
          {formatted.mapHref && (
            <div>
              <a
                href={formatted.mapHref}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                View on map
              </a>
            </div>
          )}

          {/* Pricing */}
          <div className="pt-2">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Price Info</h3>
            {formatted.prices.length === 0 ? (
              <p className="text-sm text-gray-500">-</p>
            ) : (
              <ul className="space-y-1">
                {formatted.prices.map((price, idx) => (
                  <li
                    key={idx}
                    className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2"
                  >
                    <span className="text-sm text-gray-700">{price?.name}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {price?.currency} {price?.amount}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50">
          {isSuperAdmin ? (
            <MyButton
              title={event?.status === 1 ? "Make Private" : "Make Public"}
              className={`py-2 text-xs px-5 rounded-xl ${event?.status === 1
                  ? "bg-gray-900 hover:bg-gray-700"
                  : "bg-amber-600 hover:bg-amber-500"
                }`}
              onClick={() => {
                setShowPreview(false);
                suspension(event);
              }}
            />
          ) : <span />}

          <div className="flex items-center gap-2">
            <Link to={editRoute}>
              <MyButton
                title="Edit"
                className="py-2 text-xs px-5 bg-blue-700 hover:bg-blue-600 rounded-xl"
                onClick={() => onEdit(event)}
              />
            </Link>
            <MyButton
              title="Delete"
              className="py-2 text-xs px-5 bg-red-700 hover:bg-red-600 rounded-xl"
              onClick={() => {
                setShowPreview(false);
                setShowDeleteModal(true);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Preview;
